import type { Event, MCQConfig, Question, QuestionType } from "./types";

/**
 * Stage view catalogue. Pure: no React, importable by mcp/.
 * Each question type offers up to three views; `compare` needs the event's
 * segment question ("Vergleichsfrage") and is never offered on that question.
 */
export type StageViewKey =
  | "bars"
  | "dots"
  | "compare"
  | "scale"
  | "distribution"
  | "sentiment"
  | "themes"
  | "voices"
  | "quotes";

export type StageViewDef = {
  key: StageViewKey;
  label: string;
  needsSegment: boolean;
};

const COMPARE: StageViewDef = { key: "compare", label: "Vergleich", needsSegment: true };

const CATALOGUE: Record<QuestionType, StageViewDef[]> = {
  multiple_choice: [
    { key: "bars", label: "Balken", needsSegment: false },
    { key: "dots", label: "Punkte", needsSegment: false },
    COMPARE,
  ],
  likert: [
    { key: "distribution", label: "Verteilung", needsSegment: false },
    { key: "sentiment", label: "Stimmung", needsSegment: false },
    COMPARE,
  ],
  nps: [
    { key: "distribution", label: "Verteilung", needsSegment: false },
    { key: "sentiment", label: "Stimmung", needsSegment: false },
    COMPARE,
  ],
  polar: [
    { key: "scale", label: "Waage", needsSegment: false },
    { key: "dots", label: "Punkte", needsSegment: false },
    COMPARE,
  ],
  open_text: [
    { key: "themes", label: "Themen", needsSegment: false },
    { key: "voices", label: "Stimmen", needsSegment: false },
    { key: "quotes", label: "Zitate", needsSegment: false },
  ],
};

/** A segment question is a single-select multiple_choice question without "other". */
export function isEligibleSegmentQuestion(q: Question): boolean {
  if (q.question_type !== "multiple_choice") return false;
  const cfg = q.config as MCQConfig;
  return cfg.multi_select !== true && cfg.allow_other !== true;
}

export function hasSegmentFor(
  event: Pick<Event, "segment_question_id">,
  question: Pick<Question, "id">,
): boolean {
  return !!event.segment_question_id && event.segment_question_id !== question.id;
}

export type ViewOptions = {
  /** When false, the open_text "themes" view is withheld (no cluster run yet);
   *  undefined keeps it (callers without cluster knowledge, e.g. the insights page). */
  hasClusters?: boolean;
};

export function viewsFor(
  question: Question,
  event: Pick<Event, "segment_question_id">,
  opts: ViewOptions = {},
): StageViewDef[] {
  const all = CATALOGUE[question.question_type] ?? CATALOGUE.open_text;
  const withSegment = hasSegmentFor(event, question);
  return all.filter(
    (v) => (!v.needsSegment || withSegment) && (v.key !== "themes" || opts.hasClusters !== false),
  );
}

export function resolveView(
  question: Question,
  event: Pick<Event, "segment_question_id">,
  opts: ViewOptions = {},
): StageViewDef {
  const views = viewsFor(question, event, opts);
  const stored = question.stage_view;
  return views.find((v) => v.key === stored) ?? views[0];
}
